// ✅ filepath: src/components/dashboard/ChartCard.jsx
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import LegendPills from "./LegendPills";
import CustomTooltip from "./CustomTooltip";
import { buildMonthTicks } from "../../utils/date";
import { toDollars } from "../../utils/number";

export default function ChartCard({
  tab,
  revenueChart,
  reservationsChart,
  utcToday,
  yesterday,
}) {
  const source = tab === "Revenue" ? revenueChart : reservationsChart;

  const now = new Date();
  const currYear = now.getUTCFullYear();
  const currMonth = now.getUTCMonth();

  // ✅ Proper month range
  const firstOfMonth = new Date(Date.UTC(currYear, currMonth, 1));
  const endOfMonth = new Date(Date.UTC(currYear, currMonth + 1, 0));
  const prevMonthFirst = new Date(Date.UTC(currYear, currMonth - 1, 1));
  const prevMonthEnd = new Date(Date.UTC(currYear, currMonth, 0));

  const today = utcToday;
  const yday = yesterday;

  // ✅ Build prev-month series aligned by day-of-month (1..daysInPrevMonth)
  const daysInPrevMonth = prevMonthEnd.getUTCDate();

  // Map current series points that carry a prev value to day-of-month
  const prevMap = new Map();
  source.forEach((p) => {
    if (p?.prev != null) {
      const day = new Date(p.x).getUTCDate();
      prevMap.set(day, p.prev);
    }
  });

  const prevOnly = [];
  let lastPrev = null;
  for (let d = 1; d <= daysInPrevMonth; d += 1) {
    const v = prevMap.has(d) ? prevMap.get(d) : lastPrev;
    if (v != null) lastPrev = v;
    prevOnly.push({ day: d, prev: v ?? null });
  }

  const actualOnly = source
    .filter((p) => p.x <= yday.getTime())
    .map((p) => ({
      day: new Date(p.x).getUTCDate(),
      curr: p.curr,
      date: p.date,
    }));

  const pendingOnly = [];
  for (
    let d = new Date(yday.getTime() + 24 * 3600 * 1000);
    d <= endOfMonth;
    d.setUTCDate(d.getUTCDate() + 1)
  ) {
    pendingOnly.push({
      day: d.getUTCDate(),
      currPending: null,
      date: d.toISOString().slice(0, 10),
    });
  }

  // ✅ Merge data by "day" number (1–31)
  const byDay = new Map();
  const merge = (arr) => {
    arr.forEach((obj) => {
      const base = byDay.get(obj.day) || { day: obj.day };
      byDay.set(obj.day, { ...base, ...obj });
    });
  };
  merge(prevOnly);
  merge(actualOnly);
  merge(pendingOnly);

  const unified = Array.from(byDay.values()).sort((a, b) => a.day - b.day);

  // ============================================================================

  // ✅ X-axis ticks (always 1–31)
  const ticks = buildMonthTicks(firstOfMonth).map((t) =>
    new Date(t).getUTCDate()
  );
  const guideX = yday.getUTCDate();

  const monthNow = now.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const prevMonthDate = new Date(Date.UTC(currYear, currMonth - 1, 1));
  const monthPrev = prevMonthDate.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  // ✅ Responsive tick formatting
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 640);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const compactUSD = (cents) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(toDollars(cents));

  const yTickFormatter =
    tab === "Revenue"
      ? (v) => (isMobile ? compactUSD(v) : `$${toDollars(v).toFixed(2)}`)
      : (v) => Number(v).toLocaleString("en-US");

  return (
    <div className="relative w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-5">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
        {tab} – Month to Date{" "}
        <span className="text-blue-500 font-medium">
          ({monthNow.split(" ")[0]})
        </span>
      </h2>
      <LegendPills monthNow={monthNow} monthPrev={monthPrev} />

      <div className="h-[360px] sm:h-[460px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={unified}
            margin={
              isMobile
                ? { top: 6, right: 12, left: 44, bottom: 22 }
                : { top: 6, right: 28, left: 56, bottom: 30 }
            }
          >
            <ReferenceArea
              x1={guideX + 0.5}
              x2={31}
              y1="auto"
              y2="auto"
              fill="#f3f4f6"
              fillOpacity={0.45}
              ifOverflow="hidden"
            />
            <CartesianGrid
              stroke="#e5e7eb"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              type="number"
              dataKey="day"
              domain={[1, 31]}
              ticks={ticks}
              tickFormatter={(v) => v}
              tick={{ fontSize: isMobile ? 10 : 12, fill: "#6b7280" }}
              label={{
                value: "Day of Month",
                position: "insideBottom",
                offset: isMobile ? -6 : -10,
                style: { fontSize: isMobile ? 10 : 12, fill: "#374151" },
              }}
            />
            <YAxis
              tickFormatter={yTickFormatter}
              width={isMobile ? 54 : 92}
              tick={{ fontSize: isMobile ? 10 : 12, fill: "#6b7280" }}
              tickMargin={isMobile ? 4 : 8}
              label={{
                value:
                  tab === "Revenue"
                    ? isMobile
                      ? "Revenue ($)"
                      : "Total Revenue ($)"
                    : "Reservations",
                angle: -90,
                position: "insideLeft",
                offset: isMobile ? 8 : 12,
                style: { fontSize: isMobile ? 10 : 12, fill: "#374151" },
              }}
            />
            <Tooltip
              content={<CustomTooltip mode={tab} />}
              cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 2" }}
            />
            <ReferenceLine x={guideX} stroke="#9ca3af" strokeDasharray="4 4" />

            <Line
              type="monotone"
              dataKey="prev"
              stroke="#94a3b8"
              strokeWidth={2}
              dot={false}
              name={monthPrev}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="curr"
              stroke="#2563eb"
              strokeWidth={3}
              dot={{ r: 4, fill: "#2563eb", stroke: "#fff", strokeWidth: 2 }}
              activeDot={{
                r: 6,
                fill: "#2563eb",
                stroke: "#fff",
                strokeWidth: 2,
              }}
              name={monthNow}
              connectNulls={false}
              isAnimationActive
            />
            <Line
              type="monotone"
              dataKey="currPending"
              stroke="#2563eb"
              strokeWidth={2}
              strokeDasharray="6 6"
              dot={{ r: 0 }}
              activeDot={{
                r: 5,
                fill: "#fff",
                stroke: "#2563eb",
                strokeWidth: 2,
              }}
              isAnimationActive={false}
              name="Pending"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
